import React, { useEffect, useRef, useState } from 'react';
import { playWhoosh, playPop } from 'common/utils/sfx';
import styles from './BottleSpinBoard.module.css';
import { seededRandom } from 'common/utils/seededRandom';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

/**
 * 🍾 병 돌리기 보드
 *  - N 명이 원형 배치
 *  - 병이 3~5초간 여러 바퀴 돌다가 감속 → 선정된 인덱스 가리키며 정지
 *  - 지목된 사람 하이라이트
 */

export interface BottleSpinBoardProps {
    names: string[];
    seed: number;                    // seed 변경 시 재회전
    animating: boolean;              // true 이면 spin 시작
    onFinish?: (targetIdx: number) => void;
    size?: number;                   // px, 기본 320
}

const PALETTE = ['#EB5757', '#F2994A', '#F2C94C', '#6FCF97', '#56CCF2', '#2F80ED', '#BB6BD9', '#EF7C9F', '#27AE60', '#8B5A3C'];


const BottleSpinBoard: React.FC<BottleSpinBoardProps> = ({ names, seed, animating, onFinish, size = 320 }) => {
    const n = names.length;
    const [angle, setAngle] = useState(0);
    const [targetIdx, setTargetIdx] = useState<number | null>(null);
    const rafRef = useRef<number | null>(null);

    useEffect(() => {
        if (!animating || n < 2) return;
        const rand = seededRandom(seed);
        const idx = Math.floor(rand() * n);
        setTargetIdx(null);
        // 병 머리가 위를 가리킴 (angle=0 → 12시) → idx=0 참여자가 맨 위. 시계방향으로 1, 2,...
        // 병 회전 각도: 시계방향으로 idx * (360/n) 이면 idx 참여자 가리킴. + 4~6 바퀴 추가.
        const perSlice = 360 / n;
        const loops = 5 + Math.floor(rand() * 3); // 5~7 바퀴
        const finalAngle = loops * 360 + idx * perSlice;
        const DURATION = 3500;
        const startTime = Date.now();
        const startAngle = 0;

        const ease = (t: number) => 1 - Math.pow(1 - t, 3); // cubic ease-out

        playWhoosh(DURATION / 1000);

        const tick = () => {
            const elapsed = Date.now() - startTime;
            const p = Math.min(1, elapsed / DURATION);
            setAngle(startAngle + (finalAngle - startAngle) * ease(p));
            if (p < 1) {
                rafRef.current = requestAnimationFrame(tick);
            } else {
                playPop();
                setTargetIdx(idx);
                if (onFinish) onFinish(idx);
            }
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [animating, seed]);

    if (n < 2) {
        return <div className={cls("bottle-empty")}>참여자 2명 이상 필요</div>;
    }

    const cx = size / 2;
    const cy = size / 2;
    const R = size * 0.38;
    const nameR = size * 0.44;

    return (
        <div className={cls("bottle-board")} style={{ width: size, height: size }}>
            <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
                {/* 테이블 원 */}
                <circle cx={cx} cy={cy} r={R} fill="#fff8e6" stroke="#e0c87a" strokeWidth={3} />
                <circle cx={cx} cy={cy} r={R - 8} fill="none" stroke="#e0c87a" strokeWidth={1} strokeDasharray="3 4" />

                {/* 참여자 자리 */}
                {names.map((name, i) => {
                    const a = (i / n) * 2 * Math.PI - Math.PI / 2; // 12시 기준
                    const x = cx + nameR * Math.cos(a);
                    const y = cy + nameR * Math.sin(a);
                    const isTarget = targetIdx === i;
                    const color = PALETTE[i % PALETTE.length];
                    return (
                        <g key={i}>
                            <circle cx={x} cy={y} r={isTarget ? 26 : 22} fill={color}
                                opacity={isTarget ? 1 : 0.85}
                                style={isTarget ? { filter: 'drop-shadow(0 0 12px ' + color + ')' } : undefined} />
                            <text x={x} y={y + 4} textAnchor="middle" fill="#fff" fontSize={12} fontWeight="700">
                                {name.length > 4 ? name.slice(0, 3) + '…' : name}
                            </text>
                            {isTarget && (
                                <g transform={`translate(${x - 10} ${y - 42}) scale(${20 / 24})`}>
                                    {/* IcStar (EmojiIcons) 별 path — SVG 내부라 컴포넌트 대신 path 직접 사용 */}
                                    <path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17.8 6.6 20l1-6.1L3.2 9.5l6.1-.9L12 3z" fill="#EB5757" />
                                </g>
                            )}
                        </g>
                    );
                })}

                {/* 병 — 중앙에 수직으로 놓이고, 각도만큼 회전 (시계방향) */}
                <g transform={`rotate(${angle} ${cx} ${cy})`}>
                    {/* 병 몸통 (초록) — 머리가 위(12시)를 향함 */}
                    <g transform={`translate(${cx} ${cy})`}>
                        {/* 머리 방향 표시 (위 = 지목 방향) */}
                        {/* 뚜껑 */}
                        <rect x={-4} y={-R + 12} width={8} height={10} fill="#c0392b" rx={1} />
                        {/* 목 */}
                        <rect x={-5} y={-R + 22} width={10} height={16} fill="#2C6E49" />
                        {/* 어깨 */}
                        <path d={`M-5 ${-R + 38} Q-5 ${-R + 46} -12 ${-R + 54} L12 ${-R + 54} Q5 ${-R + 46} 5 ${-R + 38} Z`} fill="#2C6E49" />
                        {/* 몸통 */}
                        <rect x={-12} y={-R + 54} width={24} height={70} fill="#2C6E49" rx={3} />
                        {/* 라벨 */}
                        <rect x={-10} y={-R + 70} width={20} height={22} fill="#F2C94C" rx={1} />
                        <rect x={-8} y={-R + 74} width={16} height={2} fill="#C0392B" />
                        {/* 바닥 */}
                        <rect x={-13} y={-R + 124} width={26} height={4} fill="#1F4D36" rx={2} />
                        {/* 빛 반사 */}
                        <rect x={-10} y={-R + 60} width={2} height={50} fill="#fff" opacity={0.25} />
                    </g>
                </g>

                {/* 중앙 점 */}
                <circle cx={cx} cy={cy} r={4} fill="#333" />
            </svg>
        </div>
    );
};

export default BottleSpinBoard;
